import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Map } from 'immutable';
import { Route, Switch } from 'react-router';
import { useSelector } from 'react-redux';

import { I18nProvider, EntityMapperContext, View } from '@uncinc/react-drupal-core';

import DesignSystem from '../DesignSystem';

import blocksMapper from '../../mappers/blocks';
import nodeMapper from '../../mappers/node';
import paragraphMapper from '../../mappers/paragraphs';
import profileMapper from '../../mappers/profile';
import taxonomyMapper from '../../mappers/taxonomy';
import viewsMapper from '../../mappers/views';

import i18next from '../../config/i18next';
import Entity from '../Entity';
import Search from '../Search';
import CustomRoute from '../CustomRoute';
import MainMenu from '../../implementations/Menu/MainMenu';
import FooterMenu from '../../implementations/Menu/FooterMenu';

import { Cookie } from '../../components';

import './style.scss';

const entityTypeMapper = {
  block: blocksMapper,
  node: nodeMapper,
  paragraph: paragraphMapper,
  profile: profileMapper,
  taxonomy: taxonomyMapper,
  view: viewsMapper,
};

const App = () => {
  const entityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));
  const entityType = useSelector((state) => state.getIn(['entity', 'entityType'], null));
  const childEntity = useSelector((state) => state.getIn([entityType, entityId], Map()));
  const rootClasses = useSelector((state) => state.getIn(['ui', 'rootClasses']));
  const language = useSelector((state) => state.getIn(['ui', 'language']));
  const entityExists = useSelector((state) => state.getIn(['entity', 'exists']));
  const path = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const [crisisBannerHeight, setCrisisBannerHeight] = useState(0);

  const animationFrameRef = useRef();
  const crisisBannerRef = useRef();
  const headerRef = useRef();
  const mainRef = useRef();

  const classes = classNames('App', rootClasses.join(' '), {
    'is-front-page': childEntity.get('is_front'),
    'not-found': !entityExists,
  }, 'route-' + path.toLowerCase().replace('/', '-').replace(' ', '_'));

  const alignHeaderToCrisisBanner = () => {
    if (crisisBannerRef.current && headerRef.current) {
      headerRef.current.style.marginTop = crisisBannerRef.current.getBoundingClientRect().height + 'px';
      mainRef.current.style.setProperty('--crisisbanner-height', `${crisisBannerRef.current.getBoundingClientRect().height}px`);
      setCrisisBannerHeight(crisisBannerRef.current.getBoundingClientRect().height);
    }

    animationFrameRef.current = requestAnimationFrame(alignHeaderToCrisisBanner);
  };

  useEffect(() => {
    // Call once to align on initial load.
    alignHeaderToCrisisBanner();

    const handleResize = () => {
      alignHeaderToCrisisBanner();
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [crisisBannerRef, headerRef]); // Dependency array ensures useEffect is called when refs change

  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(alignHeaderToCrisisBanner);

    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [crisisBannerRef, headerRef]);

  return (
    <EntityMapperContext.Provider value={entityTypeMapper}>
      <I18nProvider locale={language} i18next={i18next}>
        <div className={classes} language={language}>
          <View
            id="crisis_banner"
            display="block_1"
            forwardRef={crisisBannerRef}
          />
          <header className="SiteHeader__wrapper">
            <MainMenu
              ref={headerRef}
              crisisBannerHeight={crisisBannerHeight}
            />
          </header>
          <main ref={mainRef} className="main">
            <Switch>
              {/* Add routes here */}
              <CustomRoute path="/design-system" render={() => (<DesignSystem />)} />
              <CustomRoute path="/zoeken" render={() => (<Search />)} />
              <Route render={() => (<Entity />)} />
            </Switch>
          </main>
          <footer>
            <FooterMenu />
          </footer>
          <Cookie />
          <div id="modal-root" />
        </div>
      </I18nProvider>
    </EntityMapperContext.Provider>
  );
};

export default App;
